'use client';

import { useState } from 'react';
import { XMarkIcon, EyeIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { apiService } from '../services/api';
import Button from './button';
import { toast } from 'sonner';
import { format } from 'date-fns';

const RegisterDebtModal = ({ closeModal }) => {
	const [loading, setLoading] = useState(false);
	const [showPreview, setShowPreview] = useState(false);
	const [formData, setFormData] = useState(null);
	const [submitResult, setSubmitResult] = useState(null);

	const {
		register,
		handleSubmit,
		formState: { errors },
		watch,
		reset,
	} = useForm();

	const watchedValues = watch();

	const onSubmit = async (data) => {
		setFormData(data);
		setShowPreview(true);
	};

	const handleConfirmSubmit = async () => {
		setLoading(true);
		try {
			const response = await apiService.createDebt(formData);

			if (response.data.status === 201) {
				setSubmitResult(response.data.data);
				toast.success('Debt registered successfully!');
			} else {
				throw new Error(
					response.data.message || 'Failed to register debt',
				);
			}
		} catch (error) {
			const message =
				error.response?.data?.message ||
				error.message ||
				'Failed to register debt';
			toast.error(message);
		} finally {
			setLoading(false);
		}
	};

	const resetForm = () => {
		reset();
		setFormData(null);
		setShowPreview(false);
		setSubmitResult(null);
	};

	if (submitResult) {
		return (
			<div className='fixed inset-0 z-50 overflow-y-auto'>
				<div className='flex min-h-screen items-center justify-center p-4'>
					<div
						className='fixed inset-0 modal-backdrop'
						onClick={closeModal}></div>

					<div className='relative bg-white rounded-xl shadow-xl max-w-lg w-full'>
						{/* Header */}
						<div className='flex items-center justify-between p-6 border-b border-gray-200'>
							<h2 className='text-xl font-semibold text-gray-900'>
								Debt Registered Successfully
							</h2>
							<button
								onClick={closeModal}
								className='p-2 rounded-lg text-gray-400 hover:text-gray-500 hover:bg-gray-100 transition-colors'>
								<XMarkIcon className='h-5 w-5' />
							</button>
						</div>

						{/* Content */}
						<div className='p-6'>
							<div className='text-center mb-6'>
								<div className='w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4'>
									<svg
										className='h-8 w-8 text-green-600'
										fill='none'
										stroke='currentColor'
										viewBox='0 0 24 24'>
										<path
											strokeLinecap='round'
											strokeLinejoin='round'
											strokeWidth={2}
											d='M5 13l4 4L19 7'
										/>
									</svg>
								</div>
								<h3 className='text-lg font-medium text-gray-900 mb-2'>
									Debt Created Successfully
								</h3>
								<p className='text-sm text-gray-600'>
									The debt has been registered and consent has
									been recorded.
								</p>
							</div>

							<div className='space-y-4'>
								<div className='bg-gray-50 rounded-lg p-4'>
									<h4 className='font-medium text-gray-900 mb-3'>
										Debt Details
									</h4>
									<div className='space-y-2'>
										<div className='flex justify-between'>
											<span className='text-sm text-gray-600'>
												Debt ID
											</span>
											<span className='text-sm font-medium text-gray-900'>
												#{submitResult.debt.id}
											</span>
										</div>
										<div className='flex justify-between'>
											<span className='text-sm text-gray-600'>
												Amount
											</span>
											<span className='text-sm font-medium text-gray-900'>
												₦
												{submitResult.debt.amount.toLocaleString()}
											</span>
										</div>
										<div className='flex justify-between'>
											<span className='text-sm text-gray-600'>
												Due Date
											</span>
											<span className='text-sm font-medium text-gray-900'>
												{format(
													new Date(
														submitResult.debt.dueDate,
													),
													'MMM dd, yyyy',
												)}
											</span>
										</div>
										<div className='flex justify-between'>
											<span className='text-sm text-gray-600'>
												Status
											</span>
											<span className='status-badge status-pending'>
												{submitResult.debt.status}
											</span>
										</div>
									</div>
								</div>

								<div className='bg-gray-50 rounded-lg p-4'>
									<h4 className='font-medium text-gray-900 mb-3'>
										Customer Details
									</h4>
									<div className='space-y-2'>
										<div className='flex justify-between'>
											<span className='text-sm text-gray-600'>
												Name
											</span>
											<span className='text-sm font-medium text-gray-900'>
												{submitResult.user.name}
											</span>
										</div>
										<div className='flex justify-between'>
											<span className='text-sm text-gray-600'>
												BVN
											</span>
											<span className='text-sm font-medium text-gray-900'>
												{submitResult.user.bvn}
											</span>
										</div>
										<div className='flex justify-between'>
											<span className='text-sm text-gray-600'>
												Profile Status
											</span>
											<span
												className={`text-sm font-medium ${
													submitResult.user.isNewUser
														? 'text-blue-600'
														: 'text-green-600'
												}`}>
												{submitResult.user.isNewUser
													? 'New Customer'
													: 'Existing Customer'}
											</span>
										</div>
									</div>
								</div>
							</div>

							<div className='flex space-x-3 pt-6'>
								<Button
									type='secondary'
									variant='outlined'
									label='Register Another'
									onClick={resetForm}
									className='flex-1'
								/>
								<Button
									type='primary'
									label='Close'
									onClick={closeModal}
									className='flex-1'
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	if (showPreview) {
		return (
			<div className='fixed inset-0 z-50 overflow-y-auto'>
				<div className='flex min-h-screen items-center justify-center p-4'>
					<div
						className='fixed inset-0 modal-backdrop'
						onClick={() => setShowPreview(false)}></div>

					<div className='relative bg-white rounded-xl shadow-xl max-w-lg w-full'>
						{/* Header */}
						<div className='flex items-center justify-between p-6 border-b border-gray-200'>
							<h2 className='text-xl font-semibold text-gray-900'>
								Review Debt Registration
							</h2>
							<button
								onClick={() => setShowPreview(false)}
								className='p-2 rounded-lg text-gray-400 hover:text-gray-500 hover:bg-gray-100 transition-colors'>
								<XMarkIcon className='h-5 w-5' />
							</button>
						</div>

						{/* Content */}
						<div className='p-6'>
							<div className='space-y-4'>
								<div>
									<h3 className='font-medium text-gray-900 mb-3'>
										Debt Information
									</h3>
									<div className='bg-gray-50 rounded-lg p-4 space-y-2'>
										<div className='flex justify-between'>
											<span className='text-sm text-gray-600'>
												BVN
											</span>
											<span className='text-sm font-medium text-gray-900'>
												{formData.bvn}
											</span>
										</div>
										<div className='flex justify-between'>
											<span className='text-sm text-gray-600'>
												Amount
											</span>
											<span className='text-sm font-medium text-gray-900'>
												₦
												{Number.parseInt(
													formData.amount,
												).toLocaleString()}
											</span>
										</div>
										<div className='flex justify-between'>
											<span className='text-sm text-gray-600'>
												Due Date
											</span>
											<span className='text-sm font-medium text-gray-900'>
												{format(
													new Date(formData.dueDate),
													'MMM dd, yyyy',
												)}
											</span>
										</div>
										<div className='flex justify-between'>
											<span className='text-sm text-gray-600'>
												Creditor Account
											</span>
											<span className='text-sm font-medium text-gray-900'>
												{formData.creditorAccount}
											</span>
										</div>
									</div>
								</div>

								<div>
									<h3 className='font-medium text-gray-900 mb-3'>
										Consent Text
									</h3>
									<div className='bg-gray-50 rounded-lg p-4'>
										<p className='text-sm text-gray-700'>
											{formData.consentText}
										</p>
									</div>
								</div>
							</div>

							<div className='flex space-x-3 pt-6'>
								<Button
									type='secondary'
									variant='outlined'
									label='Back to Edit'
									onClick={() => setShowPreview(false)}
									className='flex-1'
								/>
								<Button
									type='primary'
									label={
										loading
											? 'Submitting...'
											: 'Confirm & Submit'
									}
									onClick={handleConfirmSubmit}
									loading={loading}
									disabled={loading}
									className='flex-1'
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className='fixed inset-0 z-50 overflow-y-auto'>
			<div className='flex min-h-screen items-center justify-center p-4'>
				<div
					className='fixed inset-0 modal-backdrop'
					onClick={closeModal}></div>

				<div className='relative bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden'>
					{/* Header */}
					<div className='flex items-center justify-between p-6 border-b border-gray-200'>
						<h2 className='text-xl font-semibold text-gray-900'>
							Register New Debt
						</h2>
						<button
							onClick={closeModal}
							className='p-2 rounded-lg text-gray-400 hover:text-gray-500 hover:bg-gray-100 transition-colors'>
							<XMarkIcon className='h-5 w-5' />
						</button>
					</div>

					{/* Content */}
					<div className='p-6 max-h-[calc(90vh-140px)] overflow-y-auto'>
						<form
							onSubmit={handleSubmit(onSubmit)}
							className='space-y-4'>
							<div>
								<label
									htmlFor='bvn'
									className='block text-sm font-medium text-gray-700 mb-2'>
									Customer BVN *
								</label>
								<input
									{...register('bvn', {
										required: 'BVN is required',
										pattern: {
											value: /^\d{11}$/,
											message:
												'BVN must be exactly 11 digits',
										},
									})}
									type='text'
									className='input-field w-full'
									placeholder='Enter 11-digit BVN'
									maxLength={11}
								/>
								{errors.bvn && (
									<p className='mt-1 text-sm text-red-600'>
										{errors.bvn.message}
									</p>
								)}
							</div>

							<div>
								<label
									htmlFor='amount'
									className='block text-sm font-medium text-gray-700 mb-2'>
									Debt Amount (₦) *
								</label>
								<input
									{...register('amount', {
										required: 'Amount is required',
										min: {
											value: 1000,
											message: 'Minimum amount is ₦1,000',
										},
									})}
									type='number'
									className='input-field w-full'
									placeholder='Enter debt amount'
									min='1000'
								/>
								{errors.amount && (
									<p className='mt-1 text-sm text-red-600'>
										{errors.amount.message}
									</p>
								)}
							</div>

							<div>
								<label
									htmlFor='dueDate'
									className='block text-sm font-medium text-gray-700 mb-2'>
									Due Date *
								</label>
								<input
									{...register('dueDate', {
										required: 'Due date is required',
									})}
									type='date'
									className='input-field w-full'
									min={new Date().toISOString().split('T')[0]}
								/>
								{errors.dueDate && (
									<p className='mt-1 text-sm text-red-600'>
										{errors.dueDate.message}
									</p>
								)}
							</div>

							<div>
								<label
									htmlFor='creditorAccount'
									className='block text-sm font-medium text-gray-700 mb-2'>
									Creditor Account *
								</label>
								<input
									{...register('creditorAccount', {
										required:
											'Creditor account is required',
									})}
									type='text'
									className='input-field w-full'
									placeholder='Enter creditor account number'
								/>
								{errors.creditorAccount && (
									<p className='mt-1 text-sm text-red-600'>
										{errors.creditorAccount.message}
									</p>
								)}
							</div>

							<div>
								<label
									htmlFor='consentText'
									className='block text-sm font-medium text-gray-700 mb-2'>
									Consent Text *
								</label>
								<textarea
									{...register('consentText', {
										required: 'Consent text is required',
									})}
									rows={4}
									className='input-field w-full resize-none'
									placeholder='Enter the consent text that the customer agrees to...'
								/>
								{errors.consentText && (
									<p className='mt-1 text-sm text-red-600'>
										{errors.consentText.message}
									</p>
								)}
							</div>

							<div className='flex space-x-3 pt-4'>
								<Button
									type='secondary'
									variant='outlined'
									label='Cancel'
									onClick={closeModal}
									className='flex-1'
								/>
								<Button
									type='primary'
									label='Review'
									iconRight={<EyeIcon className='h-4 w-4' />}
									onClick={handleSubmit(onSubmit)}
									className='flex-1'
								/>
							</div>
						</form>
					</div>
				</div>
			</div>
		</div>
	);
};

export default RegisterDebtModal;
